import React from 'react';

/**
 * Catches render errors in the tree below it and shows a fallback.
 *
 * This exists because the app used to decide it was broken by listening on
 * `window.unhandledrejection` — which fires for any promise nobody caught,
 * including a single failed API call, an aborted fetch, or a browser
 * extension. One failed request replaced the entire application with a 500
 * page, and since the flag was never cleared, no amount of navigating could
 * get back out of it.
 *
 * A render error is the honest version of "the app is broken": React itself
 * could not produce a screen, so there is nothing left to show. That is what
 * this catches, and nothing else.
 *
 * `resetKey` is how the page recovers: pass the current route, and navigating
 * anywhere clears the error instead of trapping the user on the fallback.
 */
export default class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null, resetKey: props.resetKey };
        this.reset = this.reset.bind(this);
    }

    static getDerivedStateFromError(error) {
        return { error };
    }

    static getDerivedStateFromProps(props, state) {
        // Navigating away from the broken screen clears the error, so Back and
        // Home actually take the user somewhere instead of re-rendering the
        // same fallback under a new URL.
        if (props.resetKey !== state.resetKey) {
            return { error: null, resetKey: props.resetKey };
        }
        return null;
    }

    componentDidCatch(error, info) {
        // Keep the component stack — the fallback deliberately shows the user
        // nothing technical, so this console entry is the only record of what
        // actually broke.
        console.error('[ErrorBoundary] Render failed:', error, info?.componentStack);
    }

    reset() {
        this.setState({ error: null });
    }

    render() {
        if (this.state.error) {
            return this.props.fallback({ error: this.state.error, reset: this.reset });
        }
        return this.props.children;
    }
}
